import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import * as RMenu from '@radix-ui/react-dropdown-menu';
import {
  Tag, AlarmClock, Sparkles, Languages, Printer, MoreHorizontal, ListTodo, Clock4, Ban, ShieldCheck, Check, Loader2, X, Copy, Reply as ReplyIcon,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import api from '../../utils/api';
import { Modal, Button, Field, Input, Textarea, Select, errMsg } from '../SupportCoordination/kit';
import { avatarColor, initials, firstOf, who, listTime, fullTime } from './mailUi';

// Pieces of the reading pane: tags, remind me / watch for reply, the AI assistant, create task,
// the ⋯ menu, a conversation's earlier messages, and swipe on phones.
const itemCls = 'flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] text-slate-700 cursor-pointer outline-none select-none data-[highlighted]:bg-slate-100 data-[disabled]:opacity-40';
const Content = ({ children, align = 'start', className }) => (
  <RMenu.Portal>
    <RMenu.Content align={align} sideOffset={4} className={clsx('z-[80] min-w-[220px] max-h-[380px] overflow-y-auto mail-scroll bg-white rounded-lg shadow-lg ring-1 ring-slate-200 p-1', className)}>{children}</RMenu.Content>
  </RMenu.Portal>
);
const Label = ({ children }) => <RMenu.Label className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{children}</RMenu.Label>;
export function TriggerBtn({ icon: Icon, label, showLabel }) {
  return (
    <RMenu.Trigger asChild>
      <button type="button" title={label} aria-label={label}
        className={clsx('flex-shrink-0 inline-flex items-center gap-1.5 h-8 rounded-md text-[13px] text-slate-700 hover:bg-slate-100 data-[state=open]:bg-slate-100', showLabel ? 'px-2.5' : 'w-8 justify-center')}>
        <Icon size={16} className="text-[#0f6cbd]" aria-hidden />{showLabel && <span className="hidden xl:inline">{label}</span>}
      </button>
    </RMenu.Trigger>
  );
}

// ── Tags ──────────────────────────────────────────────────────────────────────────────────────
export function TagMenu({ ids, current = [], tags, onChanged, onManage, trigger }) {
  const toggle = async (t) => {
    const on = !current.includes(t.id);
    try { await api.post('/mail/messages/tag', { ids, tag_id: t.id, on }); onChanged && onChanged(t.id, on); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <RMenu.Root>
      {trigger || <TriggerBtn icon={Tag} label="Tag" />}
      <Content>
        <Label>Tag</Label>
        {(tags || []).map((t) => (
          <RMenu.Item key={t.id} onSelect={(e) => { e.preventDefault(); toggle(t); }} className={itemCls}>
            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: t.color }} aria-hidden />
            <span className="flex-1">{t.name}</span>
            {current.includes(t.id) && <Check size={14} className="text-[#0f6cbd]" aria-hidden />}
          </RMenu.Item>
        ))}
        {!(tags || []).length && <p className="px-2.5 py-2 text-[12.5px] text-slate-500">No tags yet.</p>}
        <RMenu.Separator className="my-1 h-px bg-slate-100" />
        <RMenu.Item onSelect={onManage} className={itemCls}><Tag size={14} className="text-slate-500" aria-hidden /> Make and change tags</RMenu.Item>
      </Content>
    </RMenu.Root>
  );
}
export const TagChips = ({ ids, tags, small }) => {
  const list = (ids || []).map((id) => (tags || []).find((t) => t.id === id)).filter(Boolean);
  if (!list.length) return null;
  return list.map((t) => (
    <span key={t.id} className={clsx('inline-flex items-center gap-1 rounded-full font-medium', small ? 'px-1.5 py-px text-[10.5px]' : 'px-2 py-0.5 text-[11.5px]')}
      style={{ background: `${t.color}1a`, color: t.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} aria-hidden />{t.name}
    </span>
  ));
};

// ── Remind me / watch for reply ──────────────────────────────────────────────────────────────
async function addWatch(messageId, days, kind, onDone) {
  try {
    await api.post(`/mail/messages/${messageId}/watch`, { days, kind });
    toast.success(kind === 'reply' ? `Rostery will tell you if no reply comes in ${days} day${days === 1 ? '' : 's'}` : `Reminder set for ${days} day${days === 1 ? '' : 's'} from now`);
    onDone && onDone();
  } catch (e) { toast.error(errMsg(e)); }
}
// The same choices as items for the ⋯ menu (when Remind me is not on the toolbar).
export const remindItems = (messageId, sent, onDone) => [
  { header: sent ? 'Watch for a reply' : 'Remind me about this' },
  ...[[1, 'Tomorrow'], [2, 'In 2 days'], [3, 'In 3 days'], [7, 'In a week'], [14, 'In 2 weeks']].map(([d, l]) => ({ label: l, icon: Clock4, onClick: () => addWatch(messageId, d, sent ? 'reply' : 'remind', onDone) })),
  ...(sent ? [] : ['sep', { header: 'Watch for a reply' }, ...[[2, 'If no reply in 2 days'], [7, 'If no reply in a week']].map(([d, l]) => ({ label: l, icon: ReplyIcon, onClick: () => addWatch(messageId, d, 'reply', onDone) }))]),
];
export const aiItems = (onRun) => [
  { label: 'Summarise the conversation', icon: Sparkles, onClick: () => onRun('summarise') },
  { label: 'Draft a reply', icon: ReplyIcon, onClick: () => onRun('reply') },
  { label: 'Draft a short reply', icon: ReplyIcon, onClick: () => onRun('reply-brief') },
  { label: 'Translate', icon: Languages, onClick: () => onRun('translate') },
];
export function RemindMenu({ messageId, sent, onDone }) {
  const add = (days, kind) => addWatch(messageId, days, kind, onDone);
  return (
    <RMenu.Root>
      <TriggerBtn icon={AlarmClock} label="Remind me" />
      <Content>
        <Label>{sent ? 'Watch for a reply' : 'Remind me about this'}</Label>
        {[[1, 'Tomorrow'], [2, 'In 2 days'], [3, 'In 3 days'], [7, 'In a week'], [14, 'In 2 weeks']].map(([d, l]) => (
          <RMenu.Item key={d} onSelect={() => add(d, sent ? 'reply' : 'remind')} className={itemCls}><Clock4 size={14} className="text-slate-500" aria-hidden />{l}</RMenu.Item>
        ))}
        {!sent && (
          <>
            <RMenu.Separator className="my-1 h-px bg-slate-100" />
            <Label>Watch for a reply</Label>
            {[[2, 'If no reply in 2 days'], [7, 'If no reply in a week']].map(([d, l]) => (
              <RMenu.Item key={`r${d}`} onSelect={() => add(d, 'reply')} className={itemCls}><ReplyIcon size={14} className="text-slate-500" aria-hidden />{l}</RMenu.Item>
            ))}
          </>
        )}
      </Content>
    </RMenu.Root>
  );
}

// ── AI assistant ─────────────────────────────────────────────────────────────────────────────
export function AiMenu({ onRun, showLabel }) {
  return (
    <RMenu.Root>
      <TriggerBtn icon={Sparkles} label="AI" showLabel={showLabel} />
      <Content>
        <Label>AI assistant</Label>
        <RMenu.Item onSelect={() => onRun('summarise')} className={itemCls}><Sparkles size={14} className="text-violet-600" aria-hidden /> Summarise the conversation</RMenu.Item>
        <RMenu.Item onSelect={() => onRun('reply')} className={itemCls}><ReplyIcon size={14} className="text-violet-600" aria-hidden /> Draft a reply</RMenu.Item>
        <RMenu.Item onSelect={() => onRun('reply-brief')} className={itemCls}><ReplyIcon size={14} className="text-violet-600" aria-hidden /> Draft a short reply</RMenu.Item>
        <RMenu.Item onSelect={() => onRun('translate')} className={itemCls}><Languages size={14} className="text-violet-600" aria-hidden /> Translate</RMenu.Item>
      </Content>
    </RMenu.Root>
  );
}
export function AiPanel({ panel, onClose, onUseReply }) {
  if (!panel) return null;
  const title = { summarise: 'Summary', reply: 'Suggested reply', translate: 'Translation' }[panel.kind] || 'AI';
  return (
    <div className="mt-4 rounded-xl ring-1 ring-violet-200 bg-violet-50/50 overflow-hidden">
      <div className="flex items-center gap-2 px-3.5 py-2 border-b border-violet-100">
        <Sparkles size={14} className="text-violet-600" aria-hidden />
        <span className="text-[12.5px] font-semibold text-violet-900 flex-1">{title}</span>
        {panel.text && <button type="button" onClick={() => { navigator.clipboard && navigator.clipboard.writeText(panel.text); toast.success('Copied'); }} className="w-7 h-7 rounded flex items-center justify-center text-violet-700 hover:bg-violet-100" aria-label="Copy"><Copy size={13} /></button>}
        <button type="button" onClick={onClose} className="w-7 h-7 rounded flex items-center justify-center text-violet-700 hover:bg-violet-100" aria-label="Close"><X size={14} /></button>
      </div>
      <div className="px-3.5 py-3 text-[13px] text-slate-800 whitespace-pre-wrap leading-relaxed">
        {panel.loading ? <span className="flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" aria-hidden /> Thinking…</span> : panel.error ? <span className="text-rose-700">{panel.error}</span> : panel.text}
      </div>
      {panel.kind === 'reply' && panel.text && !panel.loading && (
        <div className="px-3.5 pb-3"><Button size="sm" onClick={() => onUseReply(panel.text)}>Use this reply</Button></div>
      )}
      <p className="px-3.5 pb-2 text-[11px] text-violet-800/70">Written by AI. Check it before you rely on it.</p>
    </div>
  );
}

// ── Create a Rostery task from an email ──────────────────────────────────────────────────────
export function TaskDialog({ open, onClose, message }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  // The files this email is linked to, as task links (an SC one makes it an SC task).
  const links = ((message && message.links) || []).map((l, i) => ({
    value: String(i), label: `${l.label} (${l.module === 'sc' ? 'SC' : l.module === 'pm' ? 'PM' : 'Client'}, ${l.role})`,
    link: l.module === 'sc' && l.coordination_id ? { module: 'sc', id: l.coordination_id, client_id: l.client_id, name: l.label } : { module: l.module === 'pm' ? 'pm' : 'care', id: l.client_id, client_id: l.client_id, name: l.label },
  }));
  React.useEffect(() => {
    if (!open || !message) return;
    const d = new Date(Date.now() + 2 * 86400000);
    const due = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const from = firstOf(message.from) || {};
    setF({ title: String(message.subject || 'Follow up this email').replace(/^s*((re|fw|fwd)s*:s*)+/i, '').slice(0, 200) || 'Follow up this email', dueDate: due, reminder: '', link: links.length ? '0' : '',
      description: `From ${from.name || from.address || ''} on ${fullTime(message.date)}:
${String(message.text || message.snippet || '').slice(0, 1500)}` });
  }, [open, message]);
  if (!open || !f) return null;
  const save = async () => {
    setBusy(true);
    try {
      const pick = links.find((x) => x.value === f.link);
      await api.post('/email-tasks', { title: f.title, due_date: f.dueDate || null, notes: f.description, reminder_at: f.reminder ? new Date(f.reminder).toISOString() : null, link: pick ? pick.link : null, source_message_id: message.id });
      toast((t) => (<span className="flex items-center gap-3 text-[13px]">Task made<a href="/mail?view=tasks" onClick={() => toast.dismiss(t.id)} className="font-semibold text-[#0f6cbd] hover:underline">Open Tasks</a></span>), { duration: 5000 });
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Make a task from this email" sub="It goes on your Tasks list, with this email linked. Linked to an SC participant, it is also on their SC file."
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} loading={busy} disabled={!f.title.trim()}>Make task</Button></>}>
      <div className="space-y-3">
        <Field label="Task" required><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} autoFocus /></Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Due"><Input type="date" value={f.dueDate} onChange={(e) => setF({ ...f, dueDate: e.target.value })} /></Field>
          <Field label="Remind me"><Input type="datetime-local" value={f.reminder} onChange={(e) => setF({ ...f, reminder: e.target.value })} /></Field>
        </div>
        {links.length > 0 && <Field label="Linked to"><Select value={f.link} allowEmpty emptyLabel="Nobody" onChange={(v) => setF({ ...f, link: v })} options={links} /></Field>}
        <Field label="Notes"><Textarea rows={5} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
      </div>
    </Modal>
  );
}

// ── The ⋯ menu: whatever is not on the toolbar, and the rarer actions ────────────────────────
// One item: a separator, a heading, an action, or { sub: [...] } (a menu that opens to the side).
function MenuItems({ list }) {
  return list.filter(Boolean).map((it, i) => {
    if (it === 'sep') return <RMenu.Separator key={`s${i}`} className="my-1 h-px bg-slate-100" />;
    if (it.header) return <Label key={`h${i}`}>{it.header}</Label>;
    const icon = it.icon ? <it.icon size={14} className={it.danger ? 'text-rose-600' : 'text-slate-500'} aria-hidden /> : it.dot ? <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: it.dot }} aria-hidden /> : null;
    if (it.sub) {
      return (
        <RMenu.Sub key={`${it.label}${i}`}>
          <RMenu.SubTrigger className={clsx(itemCls, 'data-[state=open]:bg-slate-100')}>{icon}<span className="flex-1">{it.label}</span><ChevronRight size={13} className="text-slate-400" aria-hidden /></RMenu.SubTrigger>
          <RMenu.Portal>
            <RMenu.SubContent sideOffset={4} className="z-[81] min-w-[210px] max-h-[360px] overflow-y-auto mail-scroll bg-white rounded-lg shadow-lg ring-1 ring-slate-200 p-1">
              <MenuItems list={it.sub} />
            </RMenu.SubContent>
          </RMenu.Portal>
        </RMenu.Sub>
      );
    }
    return (
      <RMenu.Item key={`${it.label}${i}`} onSelect={(e) => { if (it.keepOpen) e.preventDefault(); it.onClick && it.onClick(); }} disabled={it.disabled} className={clsx(itemCls, it.danger && 'text-rose-700')}>
        {icon}<span className="flex-1">{it.label}</span>{it.checked && <Check size={14} className="text-[#0f6cbd]" aria-hidden />}
      </RMenu.Item>
    );
  });
}
export function MoreMenu({ items }) {
  return (
    <RMenu.Root>
      <TriggerBtn icon={MoreHorizontal} label="More actions" />
      <Content align="end"><MenuItems list={items} /></Content>
    </RMenu.Root>
  );
}
export const MORE_ICONS = { ListTodo, Printer, Ban, ShieldCheck, Languages, Sparkles };

// ── A conversation's earlier messages, above the one open ────────────────────────────────────
export function ThreadHeader({ thread, openId, onOpen, open, onToggle }) {
  const [own, setOwn] = useState(false);
  const shown = open == null ? own : open;
  const setShown = onToggle ? () => onToggle() : setOwn;
  const others = (thread || []).filter((m) => m.id !== openId);
  if (!others.length) return null;
  return (
    <div className="mt-4 rounded-xl ring-1 ring-slate-200 overflow-hidden">
      <button type="button" onClick={() => setShown((v) => !v)} className="w-full flex items-center gap-2 px-3.5 py-2 bg-slate-50 text-[12.5px] font-semibold text-slate-700 hover:bg-slate-100">
        {shown ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />} {others.length} other message{others.length === 1 ? '' : 's'} in this conversation
      </button>
      {shown && (
        <ul className="divide-y divide-slate-100">
          {others.map((m) => {
            const p = firstOf(m.from_addr) || {};
            return (
              <li key={m.id}>
                <button type="button" onClick={() => onOpen(m.id)} className="w-full flex items-center gap-3 px-3.5 py-2 text-left hover:bg-slate-50">
                  <span className="w-7 h-7 rounded-full text-white text-[10.5px] font-bold flex items-center justify-center flex-shrink-0" style={{ background: avatarColor(p.address) }} aria-hidden>{initials(p.name, p.address)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline gap-2"><span className={clsx('text-[12.5px] truncate', !m.is_read ? 'font-semibold text-slate-900' : 'text-slate-700')}>{who(p)}</span><span className="ml-auto text-[11px] text-slate-500 flex-shrink-0">{listTime(m.msg_date)}</span></span>
                    <span className="block text-[12px] text-slate-500 truncate">{m.snippet}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ── Swipe on phones ──────────────────────────────────────────────────────────────────────────
// Returns touch handlers and the current offset; past 80px the action runs.
export function useSwipe({ onLeft, onRight, enabled }) {
  const start = useRef(null);
  const [dx, setDx] = useState(0);
  if (!enabled) return { handlers: {}, dx: 0 };
  return {
    dx,
    handlers: {
      onTouchStart: (e) => { const t = e.touches[0]; start.current = { x: t.clientX, y: t.clientY, lock: null }; },
      onTouchMove: (e) => {
        const s = start.current; if (!s) return;
        const t = e.touches[0]; const x = t.clientX - s.x; const y = t.clientY - s.y;
        if (s.lock === null && (Math.abs(x) > 8 || Math.abs(y) > 8)) s.lock = Math.abs(x) > Math.abs(y) ? 'x' : 'y';
        if (s.lock === 'x') setDx(Math.max(-140, Math.min(140, x)));
      },
      onTouchEnd: () => {
        const d = dx; start.current = null; setDx(0);
        if (d <= -80 && onLeft) onLeft(); else if (d >= 80 && onRight) onRight();
      },
    },
  };
}
